import type { ObserveHooks, RestCall } from '@bldsoft/cappie/rest'
import type {
  HttpRequestInfo,
  HttpResponseInfo,
  RequestError,
} from '@setplex/tria-api'

import { createEvent } from 'effector'
import { toRequestError, toResponseInfo } from './result'

/**
 * Request was dispatched to the transport
 */
export const request = createEvent<HttpRequestInfo>()

/**
 * Request finished with a response, either successful or not
 */
export const response = createEvent<{
  request: HttpRequestInfo
  response: HttpResponseInfo
  error?: RequestError
  json?: unknown
}>()

/**
 * Request finished without any response
 */
export const error = createEvent<{
  request: HttpRequestInfo
  error?: RequestError
}>()

/**
 * Size of a request body in bytes, used for metrics only
 */
function toBodySize(call: RestCall): number {
  const body = call.init?.body
  if (call.method === 'GET' || body === undefined) return 0
  if (typeof body === 'string') return body.length
  try {
    return JSON.stringify(body).length
  } catch {
    return 0
  }
}

function toRequestInfo(call: RestCall, url = call.url): HttpRequestInfo {
  return {
    method: call.method,
    url,
    bodySize: toBodySize(call),
  }
}

/**
 * Observe every call of the API client, to report it as HTTP events.
 * Compose in `restify`, not in transport `enhance`.
 */
export const observe: ObserveHooks = {
  request(call) {
    request(toRequestInfo(call))
  },

  result(result, call) {
    switch (result.kind) {
      // only these kinds are answered by the backend
      case 'success':
      case 'api-error':
      case 'http-error': {
        response({
          request: toRequestInfo(call, result.http.request.url),
          response: toResponseInfo(result.http),
          error: toRequestError(result),
          json: result.body,
        })
        break
      }

      default: {
        error({
          request: toRequestInfo(call),
          error: toRequestError(result),
        })
      }
    }
  },
}
